import type { DocumentType, Invoice } from '../../types';
import { effectiveInvoiceStatus, openAmountCents } from '../../services/invoiceLogic';
import { OPEN_STATUSES, type Filter, type SortKey } from './invoicesViewShared';

/**
 * Die Belegliste der Rechnungsuebersicht — Filtern, Sortieren, Blaettern, Summen.
 *
 * Steht als reine Funktionen neben der Ansicht: die Faelle, in denen die Liste
 * still falsch wird (ein Storno unter "Offen", Entwuerfe am falschen Ende,
 * eine leere Seite nach dem Filtern), lassen sich so ohne DOM-Testumgebung
 * pruefen — die gibt es im Projekt nicht.
 *
 * Kundenname, Projektbezeichnung und Stichtag kommen von aussen: die Ansicht
 * haelt die Nachschlagekarten, und Anzeige, Suche und Sortierung muessen
 * dieselbe Ableitung lesen.
 */

/** Die gemerkten Filter der Belegliste. 'all' bzw. leer heisst: nicht eingeschraenkt. */
export interface InvoiceListFilters {
    filter: Filter;
    search: string;
    /** Kunden-ID oder 'all'. */
    customerFilter: string;
    typeFilter: DocumentType | 'all';
    /** Belegdatum ab, 'JJJJ-MM-TT' oder leer. */
    dateFrom: string;
    /** Belegdatum bis einschliesslich, 'JJJJ-MM-TT' oder leer. */
    dateTo: string;
}

/** Die Sortierung der Spaltenkoepfe (SortableTh). */
export interface InvoiceListSort {
    key: SortKey;
    dir: 'asc' | 'desc';
}

/** Was die Liste aus der Ansicht nachschlaegt. */
export interface InvoiceListLookups {
    /** Stichtag fuer die abgeleitete Ueberfaelligkeit (effectiveInvoiceStatus). */
    today: string;
    customerNameOf: (inv: Invoice) => string;
    projectLabelOf: (inv: Invoice) => string;
}

/** Summen der Trefferliste, in Cent. */
export interface InvoiceListTotals {
    gross: number;
    open: number;
}

/** Die Belege, die zu allen gesetzten Filtern passen — in der Reihenfolge der Eingabe. */
export const filterInvoiceList = (
    invoices: Invoice[],
    filters: InvoiceListFilters,
    { today, customerNameOf }: Pick<InvoiceListLookups, 'today' | 'customerNameOf'>,
): Invoice[] => {
    const { filter, customerFilter, typeFilter, dateFrom, dateTo } = filters;
    const term = filters.search.trim().toLowerCase();
    return invoices.filter((inv) => {
        const eff = effectiveInvoiceStatus(inv, today);
        if (filter === 'open') {
            // Der Status allein reicht nicht: ein Stornobeleg steht auf
            // "Ausgestellt", fordert aber nichts — er nimmt zurueck. Offen
            // ist, was tatsaechlich noch Geld erwartet.
            if (!OPEN_STATUSES.includes(eff) || openAmountCents(inv) <= 0) return false;
        } else if (filter !== 'all' && eff !== filter) {
            return false;
        }
        if (customerFilter !== 'all' && inv.customerId !== customerFilter) return false;
        if (typeFilter !== 'all' && inv.documentType !== typeFilter) return false;
        // Gefiltert wird ueber das Belegdatum, nicht die Faelligkeit:
        // "Was habe ich im Mai gestellt" ist die Frage — die Faelligkeit
        // haengt am Zahlungsziel und schoebe den Beleg in einen Monat, in
        // dem er gar nicht entstanden ist.
        if (dateFrom && inv.invoiceDate < dateFrom) return false;
        if (dateTo && inv.invoiceDate > dateTo) return false;
        if (!term) return true;
        return (
            (inv.invoiceNumber || '').toLowerCase().includes(term) ||
            customerNameOf(inv).toLowerCase().includes(term)
        );
    });
};

/** Der Wert, nach dem eine Spalte sortiert. Gleiche Werte gelten als gleichrangig. */
const sortValue = (
    inv: Invoice,
    key: SortKey,
    { customerNameOf, projectLabelOf }: Pick<InvoiceListLookups, 'customerNameOf' | 'projectLabelOf'>,
): string | number => {
    switch (key) {
        case 'customer':
            return customerNameOf(inv).toLowerCase();
        case 'project':
            // Belege ohne Projektbezug ans Ende der Aufsteigenden: die
            // Frage lautet "welche Projekte stehen hier", und darauf
            // ist "keines" die uninteressanteste Antwort.
            return projectLabelOf(inv).toLowerCase() || '￿';
        case 'date':
            return inv.invoiceDate;
        case 'due':
            return inv.dueDate ?? '';
        case 'gross':
            return inv.totals.grossTotalCents;
        case 'open':
            return openAmountCents(inv);
        default:
            // Entwuerfe haben noch keine Nummer und sortieren mit einem
            // hohen Zeichen ans Ende der Aufsteigenden — absteigend
            // stehen sie damit oben, und dort gehoeren sie hin: sie sind
            // das Einzige, woran noch gearbeitet wird.
            return inv.invoiceNumber || '￿';
    }
};

/**
 * Sortiert eine Kopie der Liste nach Spalte und Richtung.
 *
 * Gleichstand bleibt in der Reihenfolge der Eingabe stehen — in beide
 * Richtungen. Die Sortierung von Arrays ist stabil, und der Vergleich meldet
 * bei gleichen Werten 0 statt einer Richtung.
 */
export const sortInvoiceList = (
    rows: Invoice[],
    sort: InvoiceListSort,
    lookups: Pick<InvoiceListLookups, 'customerNameOf' | 'projectLabelOf'>,
): Invoice[] => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = sortValue(a, sort.key, lookups);
        const vb = sortValue(b, sort.key, lookups);
        if (va === vb) return 0;
        return (va < vb ? -1 : 1) * dir;
    });
};

/** Die Trefferliste der Ansicht: erst gefiltert, dann sortiert. */
export const visibleInvoiceList = (
    invoices: Invoice[],
    filters: InvoiceListFilters,
    sort: InvoiceListSort,
    lookups: InvoiceListLookups,
): Invoice[] => sortInvoiceList(filterInvoiceList(invoices, filters, lookups), sort, lookups);

/** Seitenzahl der Trefferliste — mindestens eine, auch ohne Treffer. */
export const invoiceListPageCount = (total: number, pageSize: number): number =>
    Math.max(1, Math.ceil(total / pageSize));

/** Die Seite in die Grenzen 1 .. pageCount gezogen. */
export const clampInvoiceListPage = (page: number, pageCount: number): number =>
    Math.min(Math.max(1, page), pageCount);

/** Die Zeilen der Seite `currentPage` (ab 1 gezaehlt). */
export const invoiceListPageItems = <T>(rows: T[], currentPage: number, pageSize: number): T[] =>
    rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

/**
 * Brutto und Offen ueber alle uebergebenen Belege.
 *
 * Offen ueber openAmountCents: ein ueberzahlter Beleg zaehlt mit 0, nicht
 * negativ — sonst minderte er die Summe der anderen.
 */
export const invoiceListTotals = (
    rows: ReadonlyArray<Pick<Invoice, 'totals' | 'paidCents' | 'creditedCents'>>,
): InvoiceListTotals =>
    rows.reduce<InvoiceListTotals>(
        (acc, inv) => ({
            gross: acc.gross + inv.totals.grossTotalCents,
            open: acc.open + openAmountCents(inv),
        }),
        { gross: 0, open: 0 },
    );

/**
 * Kunden-ID und Name fuer die Kundenauswahl, nach Name sortiert.
 *
 * Nur Kunden mit Beleg; der Name ist der Join-Name des Belegs, fehlt er,
 * steht die ID da.
 */
export const invoiceCustomerOptions = (
    invoices: ReadonlyArray<Pick<Invoice, 'customerId' | 'customerName'>>,
): Array<[string, string]> => {
    const byId = new Map<string, string>();
    for (const inv of invoices) {
        if (inv.customerId) byId.set(inv.customerId, inv.customerName || inv.customerId);
    }
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
};

/** Schraenkt irgendein Filter die Liste ein? Eine Suche nur aus Leerzeichen nicht. */
export const invoiceListFiltersActive = (filters: InvoiceListFilters): boolean =>
    filters.filter !== 'all' ||
    filters.customerFilter !== 'all' ||
    filters.typeFilter !== 'all' ||
    !!filters.dateFrom ||
    !!filters.dateTo ||
    !!filters.search.trim();
